// ─────────────────────────────────────────────
//  RoomMessageBox — the read-only feed at the bottom of Room Status.
//
//  Three EXISTING sources, no new collection: the room's own perOr channel
//  (already "this room's CRNA + supervising doctor", membership derived from
//  the LIVE assignment), the Timeline's directives in `tasks`, and this room's
//  condition alerts. domain/roomMessageBox does the merging; this only renders.
//
//  ⚠️ FOUR STATES, AND THEY MUST NOT LOOK ALIKE. "not this campus", "still
//  loading", "could not load" and "genuinely nothing" are four different facts
//  and only one of them is reassuring. An empty box shown for any of the other
//  three is a lie a clinician will act on. `roomMessageBoxState` decides, and
//  it fails closed — `covered` defaults to false, so forgetting to pass it
//  yields the visible refusal rather than a confident emptiness.
//
//  ⚠️ NEVER `scrollIntoView` TO PIN THE TRANSCRIPT. It scrolls every scrollable
//  ancestor, which on this page yanks the whole screen. Set `scrollTop` on the
//  container itself, and only when the reader is already at the bottom —
//  otherwise a new message drags them away from the line they were reading.
// ─────────────────────────────────────────────
import React, { useEffect, useRef } from "react";
import mobile, { px } from "../../theme/tokens.mobile";
import { ROOM_MESSAGE_COPY } from "../../domain/roomMessageBox";

const { color, space, radius } = mobile;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const timeLabel = (at) => {
  if (!at) return "";
  try {
    return at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

export default function RoomMessageBox({
  state = "empty",
  messages = [],
  onRetry = null,
  // The supervisor page merges several rooms into one box, so each line is
  // tagged. A single-room page passes false and the tag is dropped.
  showRoomTag = false,
}) {
  const scroller = useRef(null);
  const pinned = useRef(true);

  // Keep the newest line visible only for a reader who was already at the
  // bottom. Someone scrolled up is reading something; do not steal it.
  useEffect(() => {
    const el = scroller.current;
    if (!el || !pinned.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };

  const note = (text, detail, action) => (
    <div style={s.note} data-testid="room-messages-note" data-state={state}>
      <span style={s.noteText}>{text}</span>
      {detail && <span style={s.noteDetail}>{detail}</span>}
      {action}
    </div>
  );

  return (
    <section style={s.card} aria-label="Room messages" data-testid="room-message-box">
      <h3 style={s.heading}>MESSAGES</h3>

      {state === "uncovered" && note(ROOM_MESSAGE_COPY.uncovered)}
      {state === "loading" && note(ROOM_MESSAGE_COPY.loading)}
      {state === "error" &&
        note(
          ROOM_MESSAGE_COPY.error,
          null,
          onRetry ? (
            <button type="button" style={s.retry} onClick={onRetry}>
              Retry
            </button>
          ) : null
        )}
      {state === "empty" && note(ROOM_MESSAGE_COPY.empty, ROOM_MESSAGE_COPY.emptyDetail)}

      {state === "ready" && (
        <div
          ref={scroller}
          onScroll={onScroll}
          style={{ ...s.log, scrollBehavior: prefersReducedMotion() ? "auto" : "smooth" }}
          role="log"
          aria-live="polite"
          data-testid="room-messages-log"
        >
          {messages.map((m) => (
            <div key={m.id} style={s.line} data-source={m.source}>
              <div style={s.lineHead}>
                {showRoomTag && m.roomName && (
                  <span style={s.roomTag}>{m.roomName}</span>
                )}
                <span style={s.from}>{m.from}</span>
                <span style={s.at}>{timeLabel(m.at)}</span>
              </div>
              <div style={s.text}>{m.text}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const s = {
  card: {
    marginTop: px(space.md),
    padding: px(space.md),
    background: color.surface.sunken,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
  },
  heading: {
    margin: `0 0 ${px(space.sm)} 0`,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
    color: color.text.muted,
  },
  // A bounded well, scrolled internally — the page itself must not grow a
  // second scroll axis behind it.
  log: { maxHeight: 168, overflowY: "auto" },
  line: { marginBottom: px(space.sm), minWidth: 0 },
  lineHead: { display: "flex", alignItems: "baseline", gap: px(6), minWidth: 0 },
  roomTag: {
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 800,
    color: color.accent.primary,
  },
  // The name is the only shrinkable thing in this row; everything else is
  // fixed, so give it the flex and a zero min-width or it pushes the time off.
  from: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 12,
    fontWeight: 700,
    color: color.text.muted,
  },
  at: { flexShrink: 0, fontSize: 11, color: color.text.muted },
  text: { fontSize: 13, lineHeight: 1.4, color: color.text.primary, wordBreak: "break-word" },
  note: { display: "flex", flexDirection: "column", gap: px(4) },
  noteText: { fontSize: 13, color: color.text.primary },
  noteDetail: { fontSize: 12, color: color.text.muted },
  retry: {
    marginTop: px(space.sm),
    alignSelf: "flex-start",
    minHeight: 48,
    padding: `0 ${px(space.md)}`,
    borderRadius: radius.sm,
    border: `1px solid ${color.border.default}`,
    background: color.surface.card,
    color: color.text.primary,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: "pointer",
  },
};
